import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Product photo viewer. Navigation and magnification have separate controls. */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #controller;
  #index = 0;
  #opener;
  #previousScrollY = 0;
  #opened = false;
  #loading = new WeakSet();
  #resizeObserver = new ResizeObserver(() => this.#position());

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.refs.dialog.addEventListener('close', this.#cleanup, { signal });
    this.refs.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
    this.addEventListener('zoom-swipe', (event) => this.navigate(event.detail.direction), { signal });
    this.addEventListener('zoom-change', () => this.#updateControls(), { signal });
    this.#resizeObserver.observe(this.refs.dialog);
  }

  disconnectedCallback() {
    this.close();
    this.#controller?.abort();
    this.#resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    if (!this.refs.media[index]) return;
    if (!this.refs.dialog.open) {
      const trigger = event?.target instanceof Element ? event.target.closest('button') : null;
      const gallery = event?.target instanceof Element ? this.closest('media-gallery') : null;
      if (gallery && !gallery.hasAttribute('tabindex')) gallery.setAttribute('tabindex', '-1');
      this.#opener = trigger ?? gallery ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      this.#previousScrollY = getScrollTop();
      lockScroll(this.refs.dialog);
      this.refs.dialog.showModal();
      this.#opened = true;
    }
    this.selectThumbnail(Number(index));
    this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
  }

  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#cleanup();
  }

  closeDialog() { this.close(); }

  #cleanup = () => {
    unlockScroll(this.refs.dialog);
    if (!this.#opened) return;
    this.#opened = false;
    this.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => wrapper.resetZoom());
    this.#pauseMedia(this.refs.media[this.#index]);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.dispatchEvent(new DialogCloseEvent());
  };

  #pauseMedia(media) {
    media?.querySelectorAll('video').forEach((video) => video.pause());
    media?.querySelectorAll('deferred-media, product-model').forEach((element) => element.pauseMedia?.());
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.target instanceof Element && event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault(); this.navigate(event.key === 'ArrowRight' ? 1 : -1);
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault(); this.zoomIn();
    } else if (event.key === '-') {
      event.preventDefault(); this.zoomOut();
    } else if (event.key === '0') {
      event.preventDefault(); this.resetZoom();
    }
  }

  previous() { this.navigate(-1); }
  next() { this.navigate(1); }
  navigate(direction) {
    this.selectThumbnail(Math.max(0, Math.min(this.refs.media.length - 1, this.#index + direction)));
  }
  handleThumbnailClick(index) { this.selectThumbnail(Number(index)); }
  // High resolution is requested on selection, not for every passing hover.
  handleThumbnailPointerEnter() {}

  get #wrapper() { return this.refs.media[this.#index]?.querySelector('drag-zoom-wrapper'); }
  zoomIn() { this.#wrapper?.zoomBy(0.5); }
  zoomOut() { this.#wrapper?.zoomBy(-0.5); }
  resetZoom() { this.#wrapper?.resetZoom(); }

  selectThumbnail(index) {
    if (!Number.isInteger(index) || !this.refs.media[index]) return;
    const previous = this.refs.media[this.#index];
    previous?.querySelector('drag-zoom-wrapper')?.resetZoom();
    if (index !== this.#index) this.#pauseMedia(previous);
    this.#index = index;
    this.refs.media.forEach((media, i) => {
      media.inert = i !== index;
      media.setAttribute('aria-hidden', String(i !== index));
    });
    this.refs.thumbnails.querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === index));
      button.setAttribute('aria-current', String(i === index));
    });
    const thumbnail = this.refs.thumbnails.children[index];
    if (thumbnail) {
      this.refs.thumbnails.scrollTo({ left: thumbnail.offsetLeft - (this.refs.thumbnails.clientWidth - thumbnail.clientWidth) / 2, behavior: 'instant' });
    }
    this.#position();
    this.#wrapper?.resetZoom();
    this.loadHighResolutionImage(this.refs.media[index]);
    this.#updateControls();
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #position() {
    const track = this.querySelector('.dialog-zoomed-gallery');
    if (track && this.refs.dialog.open) track.scrollTo({ left: track.clientWidth * this.#index, behavior: 'instant' });
  }

  #updateControls() {
    const wrapper = this.#wrapper;
    const level = wrapper?.zoomLevel ?? 1;
    const maximum = wrapper?.maxZoom ?? 1;
    const controls = this.querySelector('[data-zoom-controls]');
    if (controls) controls.hidden = !wrapper;
    this.querySelector('[data-zoom-out]').disabled = level <= 1;
    this.querySelector('[data-zoom-in]').disabled = level >= maximum - 0.01;
    this.querySelector('[data-zoom-reset]').disabled = level <= 1;
    this.querySelector('[data-zoom-level]').textContent = `${Math.round(level * 100)}%`;
    this.querySelector('[data-zoom-previous]').disabled = this.#index === 0;
    this.querySelector('[data-zoom-next]').disabled = this.#index === this.refs.media.length - 1;
    this.querySelector('[data-zoom-count]').textContent = `${this.#index + 1} / ${this.refs.media.length}`;
  }

  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer?.querySelector('drag-zoom-wrapper img.product-media__image');
    if (!(image instanceof HTMLImageElement) || this.#loading.has(image) || image.dataset.zoomLoaded) return;
    const source = image.getAttribute('data_max_resolution');
    if (!source) return;
    this.#loading.add(image);
    const highRes = new Image();
    highRes.onload = () => {
      this.#loading.delete(image);
      if (!image.isConnected) return;
      image.removeAttribute('srcset');
      image.src = source;
      image.dataset.zoomLoaded = 'true';
      this.#updateControls();
    };
    highRes.onerror = () => this.#loading.delete(image);
    highRes.src = source;
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
